import { useEffect, useState } from 'react';

// Chrome/Edge fire `beforeinstallprompt` once, often before React mounts, so capture it at import time.
// iOS/macOS Safari never fire it: those users get "Add to Home Screen / Add to Dock" instructions instead.

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export const SHOW_INSTALL_EVENT = 'cosmo:show-install';

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // we show our own banner/button instead of the browser mini-infobar
    deferredPrompt = event;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

// 'ios' | 'ios-in-app' | 'mac-safari' | 'other'
export function getInstallPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) {
    // In-app browsers (WhatsApp, Facebook, Instagram, etc.) cannot add to the home screen
    return /FBAN|FBAV|Instagram|WhatsApp|Line\/|Snapchat|TikTok/i.test(ua) ? 'ios-in-app' : 'ios';
  }
  const isMacSafari = /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox/.test(ua);
  const safariVersion = parseInt((ua.match(/Version\/(\d+)/) || [])[1], 10);
  if (isMacSafari && safariVersion >= 17) return 'mac-safari';
  return 'other';
}

export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const promptEvent = deferredPrompt;
  deferredPrompt = null; // the event can only be used once
  notify();
  await promptEvent.prompt();
  const { outcome } = await promptEvent.userChoice;
  return outcome; // 'accepted' | 'dismissed'
}

export function requestInstallHelp() {
  window.dispatchEvent(new Event(SHOW_INSTALL_EVENT));
}

export function useInstallPrompt() {
  const [state, setState] = useState(() => ({
    canPrompt: Boolean(deferredPrompt),
    installed: isStandalone(),
  }));

  useEffect(() => {
    const update = () => setState({ canPrompt: Boolean(deferredPrompt), installed: isStandalone() });
    listeners.add(update);
    update();
    return () => listeners.delete(update);
  }, []);

  const platform = getInstallPlatform();
  const canInstall = !state.installed && (state.canPrompt || platform !== 'other');

  return { ...state, platform, canInstall };
}
